import { Box, Paper, Table, TableBody, TableCell, TableHead, TableContainer, TableRow, Stack, Typography, TextField, SxProps, IconButton, FormControlLabel, Switch } from "@mui/material";
import React, { useEffect, useState } from "react";
import { setSelectedMetric, setSelectedMonth, setAllReviewed, setForecastStatus, setForecastNote, setSelectedTemplateType, setSelectedMonthData, setSelectedMonthForecastContentSharedUid, setSelectedSite } from '../../../../../features/forecastersSlice';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { NumericFormat } from 'react-number-format';
import { selectUserToken, setDisplayErrorSnackBar } from '../../../../../features/userSlice';
import { forecastersEndPointLogic } from '../../../../../logic/AssetManagement/Forecasters/forecastersEndPointLogic';
import store from '../../../../../store';
import HistoryIcon from '@mui/icons-material/History';
import { Theme } from "@emotion/react";
import MetricsByMonthDialogue from "../ForeCasterInfo/MetricsByMonth/MetricsByMonth";
import ForecastSkeleton from '../CreateNewForeCast/ForecastSkeleton';

interface Props {
  selectedForecaster: any;
  loading: any;
  setLoading: any;
}

export const DuplicateForecastProfit = ({ selectedForecaster, loading, setLoading }: Props) => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const selectedTemplateType = useSelector((state: any) => state?.forecasters?.selectedTemplateType);
  const selectedYear = searchParams.get("selectedYear") as any;
  const forecastStatus = useSelector((state: any) => state.forecasters.forecastStatus);
  const selectedSite = searchParams.get("selectedSite") as any;
  const forecastNote = useSelector((state: any) => state?.forecasters?.forcastNote);
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const [editableNRPU, setEditableNRPU] = useState<string | null>(null);
  const [editableFtd, setEditableFtd] = useState<string | null>(null);
  const [editableAvgNetREv, setEditableAvgNetREv] = useState<string | null>(null);
  const [editableCPAPV, setEditableCPAPV] = useState<string | null>(null);
  const [editableCVR, setEditableCVR] = useState<string | null>(null);
  const [editableCAC, setEditableCAC] = useState<string | null>(null);
  const [editableChurnRate, setEditableChurnRate] = useState<string | null>(null);
  const [editableAcquiredToFtd, setEditableAcquiredToFtd] = useState<string | null>(null);
  const [editableRsP, setEditableRsP] = useState<string | null>(null);
  const [editableRsBrNg, setEditableRsBrNg] = useState<string | null>(null);
  const forecastLastTwelveMonthsData = useSelector((state: any) => state?.forecasters?.forecastLastTwelveMonthsData);
  const LastData = forecastLastTwelveMonthsData?.query_output || [];
  const rrActiveUsersList = Math.round(LastData.filter((entry: any) => entry.month.includes("RR")).map((entry: any) => entry.active_users));

  const tableheaders = [
    { id: 0, KEY: "month", label: "Month", metric: "month", sortable: true },
    { id: 1, KEY: "nrpu", label: "NRPU", metric: "nrpu", sortable: true },
    { id: 2, KEY: "ftd", label: "FTD", metric: "ftd", sortable: true },
    { id: 3, KEY: "avg_net_rev_per_active_user", label: "Avg Net Rev To Active Users", metric: "avg_net_rev_per_active_user", sortable: true },
    { id: 4, KEY: "cpa_pv", label: "CPA PV", metric: "cpa_pv", sortable: true },
    { id: 5, KEY: "cvr", label: "CVR", metric: "CVR", sortable: true },
    { id: 6, KEY: "cac", label: "CAC", metric: "CAC", sortable: true },
    { id: 7, KEY: "active_users", label: "Active Users", metric: "active_users", sortable: true },
    { id: 8, KEY: "old_active_users", label: "Old Active Users", metric: "old", sortable: true },
    { id: 9, KEY: "acquired_active_users", label: "Acquired Users", metric: "aquired", sortable: true },
    { id: 10, KEY: "growth_target", label: "Growth Target", metric: "growth", sortable: true },
    { id: 11, KEY: "net_revenue", label: "Net Revenue", metric: "net_rev", sortable: true },
    { id: 12, KEY: "rs", label: "RS", metric: "rs_income_dollar", sortable: true },
    { id: 13, KEY: "rs_pv", label: "RS PV", metric: "rs_pv", sortable: true },
    { id: 14, KEY: "cpa", label: "CPA Income", metric: "cpa_income_dollar", sortable: true },
    { id: 15, KEY: "total_income", label: "Total Income", metric: "total_income", sortable: true },
    { id: 16, KEY: "clicks", label: "Clicks", metric: "clicks", sortable: true },
    { id: 17, KEY: "epc_cpa", label: "EPC CPA", metric: "EPC_CPA", sortable: true },
    { id: 18, KEY: "epc_rs", label: "EPC RS", metric: "EPC_RS", sortable: true },
    { id: 19, KEY: "epc", label: "EPC", metric: "EPC", sortable: true },
    { id: 20, KEY: "cpc", label: "CPC", metric: "CPC", sortable: true },
    { id: 21, KEY: "profit", label: "Profit", metric: "profit", sortable: true },
    { id: 22, KEY: "spend", label: "Spend", metric: "spend", sortable: true },
    { id: 23, KEY: "avg_rs_deal_p", label: "Avg RS Percent", metric: "avg_rs_deal_p", sortable: true },
    { id: 24, KEY: "churn_rate", label: "Churn Rate", metric: "churn_rate", sortable: true },
    { id: 25, KEY: "br_to_ng_active_rs_ratio", label: "RS BR to BR Active Percent", metric: "br_to_ng_active_rs_ratio", sortable: true },
    { id: 26, KEY: "ratio_between_acquired_to_ftd", label: "Acquired to FTD", metric: "ratio_between_acquired_to_ftd", sortable: true },
  ];

  const sortedRows = selectedForecaster?.forecaster_content || 0;
  const getQuarterStartDate = (month: number, year: number) => {
    const quarterStartMonth = Math.floor(month / 3) * 3; // Calculate the first month of the quarter
    return new Date(year, quarterStartMonth, 15); // Return the 15th of the first month of the quarter
  };

  const isMonthPassed = (rowMonth: string) => {
    if (!rowMonth || !rowMonth.includes('-')) {
      return false; // Return false if rowMonth is undefined or not in the expected format
    }
    const [, month] = rowMonth.split('-').map(Number);
    if (isNaN(month)) {
      return false; // Return false if month is not a valid number
    }
    const quarterStartDate = getQuarterStartDate(month - 1, selectedYear);
    const currentDate = new Date();
    return currentDate > quarterStartDate;
  };

  const modifiedRows = sortedRows?.map((row: any) => {
    const [, month] = row?.month.split('-').map(Number);
    const monthName = monthNames[month - 1]; // Get month name for display
    return { ...row, displayMonth: monthName }; // Add displayMonth for UI
  });

  // Sort the modifiedRows by month before setting state
  const sortedModifiedRows = modifiedRows.sort((a: any, b: any) => {
    const monthIndexA = monthNames.indexOf(a.displayMonth);
    const monthIndexB = monthNames.indexOf(b.displayMonth);
    return monthIndexA - monthIndexB;
  });

  const editableRows = sortedModifiedRows.map((row: any) => ({ ...row }));

  const formatNumber = (value: number | string) => {
    if (typeof value === 'number') {
      return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
        useGrouping: true
      }).format(value);
    }
    return value;
  };


  const calculateInitialValues = (month: string, editableRows: any[]) => {
    const rowData = editableRows.find((row: any) => row.displayMonth === month);
    if (!rowData) return null;

    return {
      growth: parseFloat(rowData.growth_target),
      cpaPv: parseFloat(rowData.cpa_pv),
      avgNetRevPerActiveUser: parseFloat(rowData.avg_net_rev_per_active_user),
      churnRate: parseFloat(rowData.churn_rate),
      ratioBetweenAcquiredToFtd: parseFloat(rowData.ratio_between_acquired_to_ftd),
      avgRsP: parseFloat(rowData.avg_rs_deal_p),
      rsBrToRsActiveRatio: parseFloat(rowData.br_to_ng_active_rs_ratio),
      cvr: parseFloat(rowData.cvr),
      profit: parseFloat(rowData.profit),
      ftd: parseFloat(rowData.ftd),
      oldActiveUsers: parseFloat(rowData.old_active_users),
      acquiredUsers: parseFloat(rowData.acquired_active_users),
      rsPv: parseFloat(rowData.rs_pv),
      estRs: parseFloat(rowData.rs),
      estCpaIncome: parseFloat(rowData.cpa),
      clicks: parseFloat(rowData.clicks),
      cac: parseFloat(rowData.cac),
      spend: parseFloat(rowData.spend),
      totalIncome: parseFloat(rowData.total_income),
      epcCpa: parseFloat(rowData.epc_cpa),
      epcRs: parseFloat(rowData.epc_rs),
      epc: parseFloat(rowData.epc),
      cpc: parseFloat(rowData.cpc),
      activeUsers: parseFloat(rowData.active_users),
      estNetRev: parseFloat(rowData.net_revenue),
      nrpu: parseFloat(rowData.nrpu) || 0,
    };
  };

  const [monthlyData, setMonthlyData] = useState(() => {
    return editableRows.reduce((acc: any, row: any) => ({
      ...acc,
      [row.displayMonth]: {
        ...calculateInitialValues(row.displayMonth, editableRows),
        displayMonth: row.displayMonth,
        content_shared_uid: row?.content_shared_uid,
        month: row.month,
      }
    }), {});
  });

  const calculateValues = (month: string, newValues: any, forcePreviousMonthActiveUsers?: number) => {
    const data = { ...monthlyData[month], ...newValues };
    let previousMonthActiveUsers;
    const monthIndex = monthNames.indexOf(month);
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    // const currentYear = currentDate.getFullYear();
    const currentDay = currentDate.getDate();
    const selectedYearNum = Number(selectedYear);

    // If forcePreviousMonthActiveUsers is provided, use that value
    if (forcePreviousMonthActiveUsers !== undefined) {
      previousMonthActiveUsers = forcePreviousMonthActiveUsers;
    } else {
      // Determine if we're in early Q3 (July 1-15)
      const isEarlyQ3 = currentMonth === 6 && currentDay <= 15;

      // Check if the month is passed according to isMonthPassed
      const monthNum = monthIndex + 1;
      const paddedMonth = monthNum.toString().padStart(2, '0');
      const monthStr = `${selectedYear}-${paddedMonth}`;
      const isPassed = isMonthPassed(monthStr);
      if (isPassed) {
        // For passed months, use the RR data
        previousMonthActiveUsers = rrActiveUsersList;
      } else {
        // For future months, use cascading calculation
        if (monthIndex === 0) {
          // January is the first month, use RR data
          previousMonthActiveUsers = rrActiveUsersList;
        } else {
          // For other months, use the previous month's calculated active_users
          const prevMonthName = monthNames[monthIndex - 1];

          // Check if previous month is passed or already calculated
          const prevMonthStr = `${selectedYear}-${(monthIndex).toString().padStart(2, '0')}`;
          const isPrevMonthPassed = isMonthPassed(prevMonthStr);

          if (isPrevMonthPassed) {
            // If previous month is passed, use data from LastYearData
            // For Scenario 2: If we're in early Q3 (July 1-15), use June's data from last 12 month forecast
            if (isEarlyQ3 && monthIndex === 6) { // July
              const juneData = LastData.find((entry: any) => {
                const [year, month] = entry.month.split('-').map(Number);
                return month === 6 && year === selectedYearNum; // June of selected year
              });
              previousMonthActiveUsers = juneData?.active_users || rrActiveUsersList;
            } else {
              previousMonthActiveUsers = rrActiveUsersList;
            }
          } else if (monthlyData[prevMonthName]) {
            // If previous month is already calculated, use its active_users
            previousMonthActiveUsers = monthlyData[prevMonthName].activeUsers;
          } else {
            // Fallback to RR data if previous month data is not available
            previousMonthActiveUsers = rrActiveUsersList;
          }
        }
      }
    }
    // Always recalculate oldActiveUsers based on the provided or calculated previousMonthActiveUsers
    data.oldActiveUsers = Math.round((1 - (data.churnRate)) * Math.round(previousMonthActiveUsers));

    if (selectedTemplateType === 2 || selectedTemplateType === 3) {
      // For templates 2 and 3, FTD is fixed and growth is calculated
      data.growth = (data.oldActiveUsers + data.acquiredUsers) / Math.round(previousMonthActiveUsers) - 1;
    } else {
      // For template 1, growth is fixed and FTD is calculated
      data.ftd = Math.round((((1 / (1 - (data.churnRate)) - 1) * data.oldActiveUsers + (data.growth) * Math.round(previousMonthActiveUsers)) / (data.ratioBetweenAcquiredToFtd)));
    }

    // Recalculate all dependent values
    data.acquiredUsers = ((data.ratioBetweenAcquiredToFtd) * data.ftd);
    data.activeUsers = data.oldActiveUsers + data.acquiredUsers;

    // Ensure growth is consistent with activeUsers change for all templates
    if (selectedTemplateType === 2 || selectedTemplateType === 3) {
      data.growth = data.activeUsers / Math.round(previousMonthActiveUsers) - 1;
    }

    data.estNetRev = data.avgNetRevPerActiveUser * data.activeUsers * (data.rsBrToRsActiveRatio);
    data.estRs = (data.estNetRev * (data.avgRsP));
    data.rsPv = (data.estRs / data.ftd);
    data.estCpaIncome = data.cpaPv * data.ftd;
    data.totalIncome = (data.estCpaIncome + data.estRs);
    data.clicks = Math.round(data.ftd / (data.cvr));
    if (selectedTemplateType === 1 || selectedTemplateType === 2) {
      // For templates 1 and 2, profit is fixed and spend/cac are calculated
      data.spend = data.totalIncome - data.profit;
      data.cac = (data.spend / data.ftd);
    } else {
      // For template 3, CAC is fixed and profit/spend are calculated
      data.spend = (data.cac * data.ftd);
      data.profit = (data.totalIncome - data.spend);
    }
    data.epcRs = (data.estRs / data.clicks);
    data.epcCpa = (data.estCpaIncome / data.clicks);
    data.epc = (data.epcCpa + data.epcRs);
    data.cpc = (data.spend / data.clicks);
    data.nrpu = newValues.nrpu !== undefined ? newValues.nrpu : data.nrpu || 0;
    return data;
  };

  const handleInputChange = (month: string, field: string, value: number) => {
    let updatedMonthlyData = { ...monthlyData }; // Start with the changed month
    const monthIndex = monthNames.indexOf(month);
    updatedMonthlyData[month] = calculateValues(month, { [field]: value });  // Update the current month first

    for (let i = monthIndex + 1; i < monthNames.length; i++) { // Recalculate all subsequent months
      const nextMonth = monthNames[i];
      if (updatedMonthlyData[nextMonth]) { // Only update if the month exists in monthlyData
        const prevMonth = monthNames[i - 1]; // Get the previous month and its active users
        const prevMonthActiveUsers = updatedMonthlyData[prevMonth].activeUsers;
        updatedMonthlyData[nextMonth] = calculateValues(nextMonth, updatedMonthlyData[nextMonth], prevMonthActiveUsers);  // Recalculate with the forced previous month's active users
      }
    }
    setMonthlyData(updatedMonthlyData);
  };

  const onRatioAcquiredToFtdChange = (e: any, month: string) => {
    handleInputChange(month, 'ratioBetweenAcquiredToFtd', parseFloat(e.target.value));
  };

  const onAvgNetRevPerActiveUserChange = (e: any, month: string) => {
    handleInputChange(month, 'avgNetRevPerActiveUser', parseFloat(e.target.value));
  };

  const onCpaPvChange = (e: any, month: string) => {
    handleInputChange(month, 'cpaPv', parseFloat(e.target.value));
  };

  const onCvrChange = (e: any, month: string) => {
    handleInputChange(month, 'cvr', parseFloat(e.target.value));
  };

  const onAvgRsPChange = (e: any, month: string) => {
    handleInputChange(month, 'avgRsP', parseFloat(e.target.value));
  };

  const onRsBrToRsActiveRatio = (e: any, month: string) => {
    handleInputChange(month, 'rsBrToRsActiveRatio', parseFloat(e.target.value));
  };

  const onChurnRateChange = (e: any, month: string) => {
    handleInputChange(month, 'churnRate', parseFloat(e.target.value));
  };

  const onFtdChange = (e: any, month: string) => {
    handleInputChange(month, "ftd", parseFloat(e.target.value));
  };

  const onCACChange = (e: any, month: string) => {
    handleInputChange(month, 'cac', parseFloat(e.target.value));
  }

  const onNrpuChange = (e: any, month: string) => {
    handleInputChange(month, "nrpu", parseFloat(e.target.value) || 0);
  };

  const isMonthEditable = (monthName: string) => {
    const currentDate = new Date();
    const monthIndex = monthNames.indexOf(monthName);
    const currentYear = currentDate.getFullYear();

    // If selected year is future year, all months are editable
    if (Number(selectedYear) > currentYear) {
      return true;
    }

    // If selected year is past year, no months are editable
    if (Number(selectedYear) < currentYear) {
      return false;
    }

    // For current year, apply the quarter logic
    const currentQuarter = Math.floor(currentDate.getDate() > 15 ? currentDate.getMonth() / 3 : (currentDate.getMonth() - 1) / 3);

    // Block all forecasts if we're in Q4 after 15th Oct of the selected year
    if (currentDate.getMonth() >= 9 && currentDate.getDate() > 15) {
      return false;
    }

    const lockedMonths = (currentQuarter + 1) * 3;
    return monthIndex >= lockedMonths;
  };

  const handleCreateForecast = async (status: 'draft' | 'publish') => {
    try {
      setLoading(true);
      const forecastData = Object.entries(monthlyData).filter(([month]) => isMonthEditable(month))
        .map(([month, data]) => {
          const monthIndex = monthNames.indexOf(month) + 1;
          const paddedMonth = monthIndex.toString().padStart(2, '0');
          const typedData = data as any;
          return {
            month: `${selectedYear}-${paddedMonth}-01`,
            spend: typedData.spend,
            clicks: typedData.clicks,
            cpc: typedData.cpc,
            ftd: Number(typedData.ftd.toFixed(0)),
            cvr: Number(typedData.ftd.toFixed(0)) / typedData.clicks,
            cac: typedData.cac,
            profit: typedData.profit,
            cpa: typedData.estCpaIncome,
            rs: typedData.estRs,
            total_income: typedData.totalIncome,
            cpa_pv: typedData.cpaPv,
            rs_pv: typedData.rsPv,
            pv: typedData.cpaPv + typedData.rsPv,
            growth_target: typedData.growth,
            active_users: typedData.activeUsers,
            old_active_users: typedData.oldActiveUsers,
            acquired_active_users: typedData.acquiredUsers,
            churn_rate: typedData.churnRate,
            ratio_between_acquired_to_ftd: typedData.ratioBetweenAcquiredToFtd,
            avg_net_rev_per_active_user: typedData.avgNetRevPerActiveUser,
            net_revenue: typedData.estNetRev,
            avg_rs_deal_p: typedData.avgRsP,
            br_to_ng_active_rs_ratio: typedData.rsBrToRsActiveRatio,
            epc_cpa: typedData.epcCpa,
            epc_rs: typedData.epcRs,
            epc: typedData.epc,
            nrpu: typedData.nrpu || 0,
          };
        });
      const currentDate = new Date(); // Block forecast creation if in Q4 after 15th Oct
      const currentYear = currentDate.getFullYear();
      if (Number(selectedYear) === currentYear && currentDate.getMonth() >= 9 && currentDate.getDate() > 15) {
        setLoading(false);
        store.dispatch(setDisplayErrorSnackBar({ message: "Forecast creation for Q4 is closed after 15th October. You can no longer create or modify forecasts for this period.", type: "info", }));
        return;
      }
      if (!oauthTokenHeader) {
        throw new Error("User doesn't have permissions to create new forecast");
      }
      const response = await forecastersEndPointLogic.createNewForecast(oauthTokenHeader, {
        year: Number(selectedYear),
        template_type: selectedTemplateType,
        status: status,
        notes: forecastNote,
        forecaster_content: forecastData
      }, selectedSite);
      if (response?.status === 201) {
        // Update the forecasters list in the background
        await forecastersEndPointLogic.getForecastersList(selectedSite, oauthTokenHeader, selectedYear, dispatch);
        // Reset all states
        dispatch(setForecastStatus(null));
        dispatch(setForecastNote(""));
        dispatch(setAllReviewed(false));
        dispatch(setSelectedTemplateType(null));
        // Show success message and navigate
        store.dispatch(setDisplayErrorSnackBar({ message: "Forecast Has Been Created Successfully", type: "success" }));
        navigate(`/assets/overview/forecaster?${new URLSearchParams(location.search).toString()}`);
      }
    } catch (error: any) {
      store.dispatch(setDisplayErrorSnackBar({ message: error?.message || "Error creating forecast", type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (forecastStatus) {
      handleCreateForecast(forecastStatus);
      dispatch(setForecastStatus(null));
    }
    // eslint-disable-next-line
  }, [forecastStatus]);

  const handleInfoIconClick = (row: any) => {
    dispatch(setSelectedMonthData(row));
    dispatch(setSelectedMonthForecastContentSharedUid(row?.content_shared_uid));
  };

  const [openMetrics, setOpenMetrics] = useState(false);

  const handleClickOpen = (metricKey: string) => {
    setOpenMetrics(true);
    dispatch(setSelectedMetric(metricKey));
    const currentMonth = new Date().getMonth();
    const formattedMonth = `${selectedYear}-0${currentMonth + 1}-01`;
    dispatch(setSelectedMonth(formattedMonth));
    if (selectedSite) {
      dispatch(setSelectedSite(selectedSite));
    } else {
      store.dispatch(setDisplayErrorSnackBar("Please select a site.No selected site found in the URL"));
    }
  };

  const handleClose = () => {
    setOpenMetrics(false);
  };

  //(A) for months that have passed
  //(R) for the first month of the current quarter (if not passed)
  //(F) for all other future months
  const getMonthIndicator = (rowMonth: string) => {
    const [year, month] = rowMonth.split("-").map(Number);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    if (isMonthPassed(rowMonth)) {
      return "A";
    } else if (year === currentYear && (month - 1) % 3 === 0) {
      return "R";
    } else {
      return "F";
    }
  };

  return (
    <>
      {loading || Object.keys(monthlyData).length === 0 ? (
        <ForecastSkeleton />
      ) : (
        <>
         <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", mt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 16px" }}>
          <Box>
            <Typography variant="h5">Metrics By Month</Typography>
            <Typography>
              To create a new forecast, fill in the metrics for every month of
              the year and save your records.
            </Typography>
          </Box>
          <Box>
            <FormControlLabel control={<Switch color="secondary" />} label="Show only forecasts" labelPlacement="start" />
          </Box>
        </Box>
        <TableContainer className="ProfitForecasterTable">
          <Table aria-label="collapsible table" size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {tableheaders.map((header) => {
                  // Decide which columns should be left-sticky
                  const isLeftSticky =
                    header.id === 0 ||
                    header.id === 1 ||
                    header.id === 2 ||
                    header.id === 3 ||
                    header.id === 4 ||
                    header.id === 5 ||
                    header.id === 6;
                  const leftOffsets: Record<number, number> = {
                    0: 0,
                    1: 114,
                    2: 201,
                    3: 277,
                    4: 508,
                    5: 606,
                    6: 683,
                  };

                  const cellSx: SxProps<Theme> = isLeftSticky
                    ? {
                      position: "sticky",
                      left: leftOffsets[header.id] ?? 0,
                      zIndex: 99,
                      backgroundColor: "#ffffff",
                      borderRight: header.id === 6 ? "1px solid #e0e0e0" : undefined,
                      color: header.id === 0 ? "#000000" : "#9C27B0",
                      padding: header.id === 0 ? "0px 8px 0px 16px!important" : "0px 8px !important",
                      whiteSpace: "nowrap",
                    } : {
                      color: "#000000",
                      padding: header.id === 0 ? "0px 8px 0px 16px!important" : "0px 8px !important",
                      whiteSpace: "nowrap",
                    };

                  return (
                    <TableCell key={header.id} align="left" sx={cellSx}>
                      <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1}>
                        {header.label !== "Month" && header.label !== "More" ? (
                          <IconButton
                            color="secondary"
                            size="small"
                            data-tracker="131"
                            onClick={() => handleClickOpen(`${header.KEY}`)}
                          >
                            <HistoryIcon sx={{ fontSize: "1rem" }} color="secondary" />
                          </IconButton>
                        ) : null}
                        {
                          <Typography sx={{ fontWeight: 500, fontSize: "14px", lineHeight: "24px", letterSpacing: "0.17px" }}>
                            {header.label}
                          </Typography>
                        }
                      </Stack>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.values(monthlyData).map((row: any, index) => (
                <React.Fragment key={index}>
                  <TableRow
                    sx={{
                      backgroundColor: isMonthPassed(row.displayMonth) ? "#EBEBEB" : "inherit",
                      "& > *": {
                        borderBottom: "1px solid rgba(224, 224, 224, 1) !important",
                      },
                      height: 35,
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: "#FFFFFF",
                        padding: "0px 8px 0px 16px !important",
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                      }}
                    >
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        {getMonthIndicator(row.month) === 'A' ? (
                          <RouterLink
                            to={`MonthlySummery?${(() => {
                              const params = new URLSearchParams(searchParams);
                              params.set("page", "0");
                              params.set("contentSharedUid", row?.content_shared_uid);
                              params.set("month", `${row?.displayMonth} ${selectedYear}`);
                              params.set("selectedMonth", row?.month);
                              return params.toString();
                            })()}`}
                            onClick={() => handleInfoIconClick(row)}
                            style={{ textDecoration: "none", color: "inherit", display: 'flex', gap: '4px' }}
                          >
                            <Typography variant="body2" color='secondary'>({getMonthIndicator(row.month)})</Typography>
                            {row?.displayMonth ? (
                              <Typography variant="body2" color='secondary' component="span" sx={{ textDecoration: 'underline' }}>{row.displayMonth}</Typography>
                            ) : ""}
                          </RouterLink>
                        ) : (
                          <>
                            <Typography variant="body2">({getMonthIndicator(row.month)})</Typography>
                            {row?.displayMonth ? (
                              <Typography variant="body2" component="span">{row.displayMonth}</Typography>
                            ) : ""}
                          </>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px !important",
                        borderBottom: "1px solid #9C27B0",
                        position: "sticky",
                        left: "114px",
                        zIndex: 3,
                      }}
                    >
                      <div onClick={() => setEditableNRPU(row.displayMonth)}
                        style={{
                          width: "100%",
                          position: "sticky",
                          left: "114px",
                          zIndex: 3,
                        }}
                      >
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          focused={editableNRPU === row.displayMonth}
                          color="secondary"
                          defaultValue={monthlyData[row.displayMonth]?.nrpu || 0}
                          onValueChange={(values) => {
                            onNrpuChange(
                              {
                                target: {
                                  value: values.floatValue ?? 0,
                                },
                              },
                              row.displayMonth
                            );
                          }}
                          suffix="$"
                          onBlur={() => setEditableNRPU(null)}
                          thousandSeparator=","
                          inputProps={{
                            readOnly: editableNRPU !== row.displayMonth,
                            style: { textAlign: "center" },
                          }}
                          decimalScale={2}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0px !important",
                              transition: "transform 0.2s",
                              ...(editableNRPU ===
                                row.displayMonth && {
                                boxShadow:
                                  "0 2px 4px -1px rgba(0,0,0,0.1)",
                                zIndex: 1,
                              }),
                              "& fieldset": {
                                border:
                                  editableNRPU ===
                                    row.displayMonth
                                    ? "1px solid #9C27B0"
                                    : "none",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  editableNRPU ===
                                    row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  editableNRPU ===
                                    row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "& input": {
                                padding: "8px !important",
                                color: "#000000DE",
                                fontSize: "0.875rem",
                                backgroundColor:
                                  editableNRPU ===
                                    row.displayMonth
                                    ? "#FFFFFF"
                                    : "#FFFFFF",
                                cursor:
                                  editableNRPU ===
                                    row.displayMonth
                                    ? "text"
                                    : "pointer",
                                transition: "background-color 0.2s",
                              },
                            },
                          }}
                        />
                      </div>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        padding: "0px !important",
                        borderBottom: "1px solid #9C27B0",
                        position: "sticky",
                        left: "201px",
                        zIndex: 3,
                      }}
                    >
                      <div
                        onClick={() => setEditableFtd(row.displayMonth)}
                        style={{
                          width: "100%",
                          position: "sticky",
                          left: "201px",
                          zIndex: 3,
                        }}
                      >
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          color="secondary"
                          defaultValue={monthlyData[row.displayMonth]?.ftd}
                          thousandSeparator=","
                          suffix=""
                          decimalScale={editableFtd ? 2 : 0}
                          onValueChange={(values) => {
                            onFtdChange(
                              { target: { value: values.floatValue } },
                              row.displayMonth
                            );
                          }}
                          focused={editableFtd === row.displayMonth}
                          onBlur={() => setEditableFtd(null)}
                          inputProps={{
                            readOnly: editableFtd !== row.displayMonth,
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0px !important",
                              transition:
                                "transform 0.2s, box-shadow 0.2s",
                              ...(editableFtd === row.displayMonth && {
                                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                zIndex: 1,
                              }),
                              "& fieldset": {
                                border:
                                  editableFtd === row.displayMonth
                                    ? "1px solid #9C27B0"
                                    : "none",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  editableFtd === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  editableFtd === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "& input": {
                                padding: "8px !important",
                                color: "#000000DE",
                                fontSize: "0.875rem",
                                backgroundColor:
                                  editableFtd === row.displayMonth
                                    ? "#FFFFFF"
                                    : "#FFFFFF",
                                cursor:
                                  editableFtd === row.displayMonth
                                    ? "text"
                                    : "pointer",
                                transition: "background-color 0.2s",
                                textAlign: "center",
                              },
                            },
                          }}
                        />
                      </div>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px !important",
                        borderBottom: "1px solid #9C27B0",
                        position: "sticky",
                        left: "277px",
                        zIndex: 4,
                      }}
                    >
                      <div onClick={() => setEditableAvgNetREv(row.displayMonth)}
                        style={{
                          width: "100%",
                          position: "sticky",
                          left: "277px",
                          zIndex: 4,
                        }}
                      >
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          focused={editableAvgNetREv === row.displayMonth}
                          color="secondary"
                          defaultValue={monthlyData[row.displayMonth]?.avgNetRevPerActiveUser}
                          decimalScale={editableAvgNetREv ? 2 : 0}
                          onValueChange={(values) => {
                            onAvgNetRevPerActiveUserChange(
                              { target: { value: values.floatValue } },
                              row.displayMonth
                            );
                          }}
                          thousandSeparator=","
                          onBlur={() => setEditableAvgNetREv(null)}
                          inputProps={{
                            readOnly: editableAvgNetREv !== row.displayMonth,
                            style: { textAlign: "center" },
                          }}
                          suffix="$"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0px !important",
                              transition: "transform 0.2s, box-shadow 0.2s",
                              ...(editableAvgNetREv ===
                                row.displayMonth && {
                                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                zIndex: 1,
                              }),
                              "& fieldset": {
                                border:
                                  editableAvgNetREv === row.displayMonth
                                    ? "1px solid #9C27B0"
                                    : "none",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  editableAvgNetREv === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  editableAvgNetREv === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "& input": {
                                padding: "8px !important",
                                color: "#000000DE",
                                fontSize: "0.875rem",
                                backgroundColor:
                                  editableAvgNetREv === row.displayMonth
                                    ? "#FFFFFF"
                                    : "#FFFFFF",
                                cursor:
                                  editableAvgNetREv === row.displayMonth
                                    ? "text"
                                    : "pointer",
                                transition: "background-color 0.2s",
                                textAlign: "center",
                              },
                            },
                          }}
                        />
                      </div>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        padding: "0px !important",
                        borderBottom: "1px solid #9C27B0",
                        position: "sticky",
                        left: "508px",
                        zIndex: 5,
                      }}
                    >
                      <div
                        onClick={() => setEditableCPAPV(row.displayMonth)}
                        style={{
                          width: "100%",
                          position: "sticky",
                          left: "508px",
                          zIndex: 5,
                        }}
                      >
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          focused={editableCPAPV === row.displayMonth}
                          color="secondary"
                          defaultValue={monthlyData[row.displayMonth]?.cpaPv}
                          decimalScale={editableCPAPV ? 2 : 0}
                          onValueChange={(values) => {
                            onCpaPvChange(
                              { target: { value: values.floatValue } },
                              row.displayMonth
                            );
                          }}
                          onBlur={() => setEditableCPAPV(null)}
                          thousandSeparator=","
                          suffix="$"
                          inputProps={{
                            readOnly: editableCPAPV !== row.displayMonth,
                            style: { textAlign: "center" },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0px !important",
                              transition: "transform 0.2s, box-shadow 0.2s",
                              ...(editableCPAPV === row.displayMonth && {
                                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                zIndex: 1,
                              }),
                              "& fieldset": {
                                border:
                                  editableCPAPV === row.displayMonth
                                    ? "1px solid #9C27B0"
                                    : "none",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  editableCPAPV === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  editableCPAPV === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "& input": {
                                padding: "8px !important",
                                color: "#000000DE",
                                fontSize: "0.875rem",
                                backgroundColor:
                                  editableCPAPV === row.displayMonth
                                    ? "#FFFFFF"
                                    : "#FFFFFF",
                                cursor:
                                  editableCPAPV === row.displayMonth
                                    ? "text"
                                    : "pointer",
                                transition: "background-color 0.2s",
                              },
                            },
                          }}
                        />
                      </div>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px !important",
                        borderBottom: "1px solid #9C27B0",
                        position: "sticky",
                        left: "606px",
                        zIndex: 3,
                      }}
                    >
                      <div
                        onClick={() => setEditableCVR(row.displayMonth)}
                        style={{
                          width: "100%",
                          position: "sticky",
                          left: "606px",
                          zIndex: 3,
                        }}
                      >
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          focused={editableCVR === row.displayMonth}
                          color="secondary"
                          defaultValue={monthlyData[row.displayMonth]?.cvr * 100}
                          onValueChange={(values) => {
                            onCvrChange(
                              {
                                target: {
                                  value: (values.floatValue ?? 0) / 100,
                                },
                              },
                              row.displayMonth
                            );
                          }}
                          thousandSeparator=","
                          onBlur={() => setEditableCVR(null)}
                          inputProps={{
                            readOnly: editableCVR !== row.displayMonth,
                            style: { textAlign: "center" },
                          }}
                          decimalScale={2}
                          suffix="%"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0px !important",
                              transition: "transform 0.2s, box-shadow 0.2s",
                              ...(editableCVR === row.displayMonth && {
                                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                zIndex: 1,
                              }),
                              "& fieldset": {
                                border:
                                  editableCVR === row.displayMonth
                                    ? "1px solid #9C27B0"
                                    : "none",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  editableCVR === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  editableCVR === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "& input": {
                                padding: "8px !important",
                                color: "#000000DE",
                                fontSize: "0.875rem",
                                backgroundColor:
                                  editableCVR === row.displayMonth
                                    ? "#FFFFFF"
                                    : "#FFFFFF",
                                cursor:
                                  editableCVR === row.displayMonth
                                    ? "text"
                                    : "pointer",
                                transition: "background-color 0.2s",
                              },
                            },
                          }}
                        />
                      </div>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        padding: "0px !important",
                        borderBottom: "1px solid #9C27B0",
                        position: "sticky",
                        left: "683px",
                        zIndex: 3,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      <div
                        onClick={() => setEditableCAC(row.displayMonth)}
                        style={{
                          width: "100%",
                          position: "sticky",
                          left: "683px",
                          zIndex: 3,
                        }}
                      >
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          color="secondary"
                          defaultValue={monthlyData[row.displayMonth]?.cac}
                          thousandSeparator=","
                          suffix=""
                          decimalScale={editableCAC ? 2 : 0}
                          onValueChange={(values) => {
                            onCACChange(
                              { target: { value: values.floatValue } },
                              row.displayMonth
                            );
                          }}
                          focused={editableCAC === row.displayMonth}
                          onBlur={() => setEditableCAC(null)}
                          inputProps={{
                            readOnly: editableCAC !== row.displayMonth,
                            style: { textAlign: "center" },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0px !important",
                              transition:
                                "transform 0.2s, box-shadow 0.2s",
                              ...(editableCAC === row.displayMonth && {
                                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                zIndex: 1,
                              }),
                              "& fieldset": {
                                border:
                                  editableCAC === row.displayMonth
                                    ? "1px solid #9C27B0"
                                    : "none",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  editableCAC === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  editableCAC === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "& input": {
                                padding: "8px !important",
                                color: "#000000DE",
                                fontSize: "0.875rem",
                                backgroundColor:
                                  editableCAC === row.displayMonth
                                    ? "#FFFFFF"
                                    : "#FFFFFF",
                                cursor:
                                  editableCAC === row.displayMonth
                                    ? "text"
                                    : "pointer",
                                transition: "background-color 0.2s",
                              },
                            },
                          }}
                        />
                      </div>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px 8px",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid rgb(207, 206, 206)",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.activeUsers ||
                        row.activeUsers
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px 8px",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid rgb(207, 206, 206)",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.oldActiveUsers ||
                        row.oldActiveUsers
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px 8px",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid rgb(207, 206, 206)",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.acquiredUsers ||
                        row.acquiredUsers
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px !important",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid #9C27B0",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.growth * 100 ||
                        row.growth * 100
                      )}%
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px !important",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid #9C27B0",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.estNetRev ||
                        row.estNetRev
                      )}
                      $
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        padding: "0px 8px",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid rgb(207, 206, 206)",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.estRs ||
                        row.rsIncomeDollar
                      )}
                      $
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        padding: "0px 8px",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid rgb(207, 206, 206)",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.rsPv || row.rsPV
                      )}
                      $
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        padding: "0px 8px",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid rgb(207, 206, 206)",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.estCpaIncome ||
                        row.cpaIncomeDollar
                      )}
                      $
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        padding: "0px 8px",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid rgb(207, 206, 206)",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.totalIncome ||
                        row.totalIncome
                      )}
                      $
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px !important",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid #9C27B0",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.clicks || row.clicks
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px !important",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid #9C27B0",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.epcCpa || row.epcCpa
                      )}
                      $
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px !important",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid #9C27B0",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.epcRs || row.epcRs
                      )}
                      $
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px !important",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid #9C27B0",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.epc || row.epc
                      )}
                      $
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px !important",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid #9C27B0",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.cpc || row.cpc
                      )}
                      $
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px 8px",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid rgb(207, 206, 206)",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.profit ||
                        row.profit
                      )}$
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0px !important",
                        backgroundColor: "#F5F5F5",
                        borderBottom: "1px solid #9C27B0",
                      }}
                    >
                      {formatNumber(
                        monthlyData[row.displayMonth]?.spend || row.spend
                      )}
                      $
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isMonthPassed(row.month) ? "#F5F5F5" : "#FFFFFF",
                        padding: "0px 8px !important",
                      }}
                    >
                      <div onClick={() => setEditableChurnRate(row.displayMonth)}>
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          focused={editableChurnRate === row.displayMonth}
                          color="secondary"
                          defaultValue={monthlyData[row.displayMonth]?.churnRate * 100}
                          onValueChange={(values) => {
                            onChurnRateChange(
                              {
                                target: {
                                  value: (values.floatValue ?? 0) / 100,
                                },
                              },
                              row.displayMonth
                            );
                          }}
                          thousandSeparator=","
                          onBlur={() => setEditableChurnRate(null)}
                          inputProps={{
                            readOnly: editableChurnRate !== row.displayMonth,
                            style: { textAlign: "center" },
                          }}
                          decimalScale={2}
                          suffix="%"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0px !important",
                              transition: "transform 0.2s, box-shadow 0.2s",
                              ...(editableChurnRate === row.displayMonth && {
                                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                zIndex: 1,
                              }),
                              "& fieldset": {
                                border:
                                  editableChurnRate === row.displayMonth
                                    ? "1px solid #9C27B0"
                                    : "none",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  editableChurnRate === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  editableChurnRate === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "& input": {
                                padding: "8px !important",
                                color: "#000000DE",
                                fontSize: "0.875rem",
                                backgroundColor:
                                  editableChurnRate === row.displayMonth
                                    ? "#FFFFFF"
                                    : "#FFFFFF",
                                cursor:
                                  editableChurnRate === row.displayMonth
                                    ? "text"
                                    : "pointer",
                                transition: "background-color 0.2s",
                              },
                            },
                          }}
                        />
                      </div>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isMonthPassed(row.month) ? "#F5F5F5" : "#FFFFFF",
                        padding: "0px 8px !important",
                      }}
                    >
                      <div onClick={() => setEditableAcquiredToFtd(row.displayMonth)}>
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          focused={editableAcquiredToFtd === row.displayMonth}
                          color="secondary"
                          defaultValue={monthlyData[row.displayMonth]?.ratioBetweenAcquiredToFtd * 100}
                          onValueChange={(values) => {
                            onRatioAcquiredToFtdChange(
                              {
                                target: {
                                  value: (values.floatValue ?? 0) / 100,
                                },
                              },
                              row.displayMonth
                            );
                          }}
                          thousandSeparator=","
                          onBlur={() => setEditableAcquiredToFtd(null)}
                          inputProps={{
                            readOnly: editableAcquiredToFtd !== row.displayMonth,
                            style: { textAlign: "center" },
                          }}
                          decimalScale={2}
                          suffix="%"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0px !important",
                              transition: "transform 0.2s, box-shadow 0.2s",
                              ...(editableAcquiredToFtd === row.displayMonth && {
                                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                zIndex: 1,
                              }),
                              "& fieldset": {
                                border:
                                  editableAcquiredToFtd === row.displayMonth
                                    ? "1px solid #9C27B0"
                                    : "none",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  editableAcquiredToFtd === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  editableAcquiredToFtd === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "& input": {
                                padding: "8px !important",
                                color: "#000000DE",
                                fontSize: "0.875rem",
                                backgroundColor:
                                  editableAcquiredToFtd === row.displayMonth
                                    ? "#FFFFFF"
                                    : "#FFFFFF",
                                cursor:
                                  editableAcquiredToFtd === row.displayMonth
                                    ? "text"
                                    : "pointer",
                                transition: "background-color 0.2s",
                              },
                            },
                          }}
                        />
                      </div>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isMonthPassed(row.month) ? "#F5F5F5" : "#FFFFFF",
                        padding: "0px 8px !important",
                      }}
                    >
                      <div onClick={() => setEditableRsP(row.displayMonth)}>
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          focused={editableRsP === row.displayMonth}
                          color="secondary"
                          defaultValue={monthlyData[row.displayMonth]?.avgRsP * 100}
                          onValueChange={(values) => {
                            onAvgRsPChange(
                              {
                                target: {
                                  value: (values.floatValue ?? 0) / 100,
                                },
                              },
                              row.displayMonth
                            );
                          }}
                          thousandSeparator=","
                          onBlur={() => setEditableRsP(null)}
                          inputProps={{
                            readOnly: editableRsP !== row.displayMonth,
                            style: { textAlign: "center" },
                          }}
                          decimalScale={2}
                          suffix="%"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0px !important",
                              transition: "transform 0.2s, box-shadow 0.2s",
                              ...(editableRsP === row.displayMonth && {
                                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                zIndex: 1,
                              }),
                              "& fieldset": {
                                border:
                                  editableRsP === row.displayMonth
                                    ? "1px solid #9C27B0"
                                    : "none",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  editableRsP === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  editableRsP === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "& input": {
                                padding: "8px !important",
                                color: "#000000DE",
                                fontSize: "0.875rem",
                                backgroundColor:
                                  editableRsP === row.displayMonth
                                    ? "#FFFFFF"
                                    : "#FFFFFF",
                                cursor:
                                  editableRsP === row.displayMonth
                                    ? "text"
                                    : "pointer",
                                transition: "background-color 0.2s",
                              },
                            },
                          }}
                        />
                      </div>
                    </TableCell>
                    <TableCell
                      align="center"
                      data-tracker="131"
                      sx={{
                        backgroundColor: isMonthPassed(row.month) ? "#F5F5F5" : "#FFFFFF",
                        padding: "0px 8px !important",
                      }}
                    >
                      <div onClick={() => setEditableRsBrNg(row.displayMonth)}>
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          focused={editableRsBrNg === row.displayMonth}
                          color="secondary"
                          defaultValue={monthlyData[row.displayMonth]?.rsBrToRsActiveRatio * 100}
                          onValueChange={(values) => {
                            onRsBrToRsActiveRatio(
                              {
                                target: {
                                  value: (values.floatValue ?? 0) / 100,
                                },
                              },
                              row.displayMonth
                            );
                          }}
                          thousandSeparator=","
                          onBlur={() => setEditableRsBrNg(null)}
                          inputProps={{
                            readOnly: editableRsBrNg !== row.displayMonth,
                            style: { textAlign: "center" },
                          }}
                          decimalScale={2}
                          suffix="%"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0px !important",
                              transition: "transform 0.2s, box-shadow 0.2s",
                              ...(editableRsBrNg === row.displayMonth && {
                                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                zIndex: 1,
                              }),
                              "& fieldset": {
                                border:
                                  editableRsBrNg === row.displayMonth
                                    ? "1px solid #9C27B0"
                                    : "none",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  editableRsBrNg === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  editableRsBrNg === row.displayMonth
                                    ? "#9C27B0"
                                    : "none",
                              },
                              "& input": {
                                padding: "8px !important",
                                color: "#000000DE",
                                fontSize: "0.875rem",
                                backgroundColor:
                                  editableRsBrNg === row.displayMonth
                                    ? "#FFFFFF"
                                    : "#FFFFFF",
                                cursor:
                                  editableRsBrNg === row.displayMonth
                                    ? "text"
                                    : "pointer",
                                transition: "background-color 0.2s",
                              },
                            },
                          }}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          <Stack spacing={1} direction="row" justifyContent="space-between" alignItems="center"></Stack>
        </TableContainer>
      </Paper>
      <MetricsByMonthDialogue open={openMetrics} handleClose={handleClose} selectedSiteFromParent={selectedSite} />
        </>
      ) }
    </>
  )
}

